<template>
  <div class="custom-input">
    <label class="input-label" v-if="$validate.DataValid(label)">{{ $t(label) }}</label>
    <v-menu
      ref="datePickerMenu"
      v-model="datePickerOpen"
      transition="scale-transition"
      offset-y
      min-width="auto"
      :close-on-content-click="false"
      :return-value.sync="date"
      :disabled="disabled"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :append-icon="!allowInput ? icon.mdiCalendarMinus : undefined"
          persistent-placeholder
          v-model="inputDate"
          v-bind="allowInput ? {} : attrs"
          v-on="allowInput ? {} : on"
          :readonly="!allowInput"
          outlined
          single-line
          :rules="rules"
          :hide-details="hideDetails"
          :placeholder="allowInput ? `YYYY${separator}MM${separator}DD` : $t(placeholder)"
          :dense="dense"
          :class="inputClass"
          @click:clear="updateDateValueFromAction(true)"
          :clearable="!disableClear && !disabled && !isReadOnly"
          :hint="$t(hint)"
          :persistent-hint="hint !== ''"
          @keypress="formatInput($event)"
          @input="onDateInput($event)"
          :disabled="disabled"
        >
          <template slot="append" v-if="allowInput">
            <v-btn icon style="margin-top: -8px;" v-on="on" v-bind="attrs">
              <v-icon size="20" class="fi fi-br-calendar-minus"></v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </template>
      <v-date-picker
        no-title
        scrollable
        v-model="date"
        :first-day-of-week="0"
        :day-format="$formatter.dayFormat"
        :locale="locale"
        @input="updateDateValue"
        :type="isYearMonthOnly ? 'month' : 'date'"
        :range="isDateRange"
        :readonly="isReadOnly"
        :min="dateMin"
        :max="dateMax"
        :allowed-dates="_isAllowDate"
        :events="_eventColor"
        :active-picker.sync="activePicker"
      >
        <template v-if="!disabledActionButtons">
          <v-spacer></v-spacer>
          <div class="d-flex justify-space-between" style="width: 100%;">
            <v-btn min-width="max-content" depressed small text color="error" @click="datePickerOpen = false">
              <v-icon class="mr-1 fi fi-rr-cross-small" small></v-icon>{{ $t('cancel') }}
            </v-btn>
            <v-btn min-width="max-content" depressed small text color="primary" @click="updateDateValueFromAction()">
              <v-icon class="mr-1 fi fi-rr-check" size="12"></v-icon>{{ $t('confirm') }}
            </v-btn>
          </div>
        </template>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import { mdiCalendarMinus } from '@mdi/js'

export default {
  name: 'FormDatePicker',
  props: {
    label: {
      type: String,
      required: false,
      default: '',
    },
    dateValue: {
      // YYYY-mm-dd | YYYY-mm
      type: String | Array,
      required: true,
      default: '',
    },
    separator: {
      type: String,
      required: false,
      default: '-',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    isYearMonthOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDateRange: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabledActionButtons: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: true,
    },
    disablePast: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableFuture: {
      type: Boolean,
      required: false,
      default: false,
    },
    customInputClass: {
      type: String,
      required: false,
      default: '',
    },
    minDate: {
      type: String, // YYYY-MM-DD
      default: '',
    },
    maxDate: {
      type: String, // YYYY-MM-DD
      default: '',
    },
    customRules: {
      type: Array,
      required: false,
      default: () => [],
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableClear: {
      type: Boolean,
      required: false,
      default: false,
    },
    hint: {
      type: String,
      required: false,
      default: '',
    },
    enableInput: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowDates: {
      type: Array,
      required: false,
      default: () => [],
    },
    disableDates: {
      type: Array,
      required: false,
      default: () => [],
    },
    holidayData: {
      type: Array,
      required: false,
      default: () => [],
    },
    pickerActive: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  computed: {
    rules() {
      let rules = [v => (this.required ? this.$validate.required(v) || this.$t('message.formRequired') : true)]
      if (this.allowInput) {
        rules = [
          ...rules,
          v => this.gfCheckRule(this.$validate.isValidDate(v, !this.required)),
          v => this._isValidAllowDate(v),
        ]
      }
      rules = rules.concat(this.customRules)
      return rules
    },
    dateMin() {
      if (this.$validate.DataValid(this.minDate)) {
        return this.minDate
      } else if (this.disablePast) {
        return this.$formatter.formatDate()
      }

      return undefined
    },
    dateMax() {
      if (this.$validate.DataValid(this.maxDate)) {
        return this.maxDate
      } else if (this.disableFuture) {
        return this.$formatter.formatDate()
      }

      return undefined
    },
    isReadOnly() {
      return this.readonly || this.$store.getters.isLoading
    },
    inputClass() {
      let classObj = {
        'pointer-none': this.$store.getters.isLoading || this.disabled || this.readonly,
      }
      if (this.$validate.DataValid(this.customInputClass)) {
        const arr = this.customInputClass.split(' ')
        arr.forEach(name => {
          if (this.$validate.DataValid(name.trim())) {
            classObj[name.trim()] = true
          }
        })
      }

      return classObj
    },
    locale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en'
        case 'cn':
          return 'zh-CN'
        default:
          return 'zh-HK'
      }
    },
    allowInput() {
      return this.enableInput && !this.isDateRange
    },
    holidayDates() {
      if (this.$validate.DataValid(this.holidayData)) {
        return this.holidayData.map(el => el.date)
      }
      return []
    },
  },
  data: () => ({
    icon: {
      mdiCalendarMinus,
    },
    datePickerOpen: false,
    date: null,
    inputDate: '',
    activePicker: null,
  }),
  watch: {
    dateValue: {
      handler(val) {
        if (this.allowInput) {
          if (this.$validate.regexDateStr(val)) {
            this.date = val.replace(/\//g, '-')
          } else {
            this.date = ''
          }

          if (this.$validate.DataValid(val)) {
            this.inputDate = val.replace(/(\/|-)/g, this.separator)
          }
        } else {
          this.date = val
          this.inputDate = this._dateFormat()
        }
      },
      immediate: true,
    },
    datePickerOpen(val) {
      if (this.pickerActive && val && !this.date) {
        setTimeout(() => (this.activePicker = 'YEAR'))
      }
    },
  },
  methods: {
    updateDateValue(val) {
      this.$emit('date-clicked', val)
      if (this.disabledActionButtons) {
        this.updateDateValueFromAction()
      }
    },
    updateDateValueFromAction(clear = false) {
      if (clear === true) {
        this.date = this.isDateRange ? [] : ''
      }

      let _date = this.date
      if (this.isDateRange) {
        if (
          this.date.length === 2 &&
          this.$validate.isDateGreater(
            this.$formatter.convertDate(this.date[0]),
            this.$formatter.convertDate(this.date[1]),
          )
        ) {
          _date = [this.date[1], this.date[0]]
        }
      }

      this.inputDate = _date
      if (this.allowInput) {
        this.inputDate = _date.replace(/(\/|-)/g, this.separator)
      }

      this.$refs.datePickerMenu.save(_date)
      this.$emit('update:dateValue', _date)
      this.$emit('changed', _date)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
      this.datePickerOpen = false
    },
    _dateFormat() {
      if (!this.$validate.DataValid(this.date)) return ''

      if (this.isDateRange) {
        if (Array.isArray(this.date)) {
          let _date = this.date
          if (
            this.date.length === 2 &&
            this.$validate.isDateGreater(
              this.$formatter.convertDate(this.date[0]),
              this.$formatter.convertDate(this.date[1]),
            )
          ) {
            _date = [this.date[1], this.date[0]]
          }

          let s = `${_date[0]} (${this.$d(this.$formatter.convertDate(_date[0]), 'weekdayShort')})`
          if (_date.length > 1 && _date[0] !== _date[1]) {
            s += ` - ${_date[1]} (${this.$d(this.$formatter.convertDate(_date[1]), 'weekdayShort')})`
          }
          return s
        }
      } else if (this.isYearMonthOnly) {
        return this.$d(this.$formatter.convertDate(`${this.date}-01`), 'yearMonth')
      }
      return `${this.date} (${this.$d(this.$formatter.convertDate(this.date), 'weekdayShort')})`
    },
    formatInput(event) {
      if (this.allowInput) {
        return this.$formatter.date(event)
      }
    },
    onDateInput(value) {
      if (this.allowInput) {
        if (this.$validate.regexDateStr(value)) {
          this.date = value.replace(/\//g, '-')
        } else {
          this.date = ''
        }
        this.$refs.datePickerMenu.save(this.date)
        this.$emit('update:dateValue', value)
        this.$emit('changed', value)
        if (this.dispatchUpdateOnChange === true) {
          this.$store.dispatch('setDataIsUpdated', true)
        }
      }
    },
    _isAllowDate(event) {
      const d = this.$formatter.convertDate(event)
      if (this.$validate.DataValid(this.dateMin)) {
        const minDate = this.$formatter.convertDate(this.dateMin)
        if (this.$validate.isDateLess(d, minDate)) {
          return false
        }
      }
      if (this.$validate.DataValid(this.dateMax)) {
        const maxDate = this.$formatter.convertDate(this.dateMax)
        if (this.$validate.isDateGreater(d, maxDate)) {
          return false
        }
      }
      if (this.$validate.DataValid(this.disableDates, true)) {
        if (this.disableDates.includes(event)) {
          return false
        }
      }
      if (this.$validate.DataValid(this.allowDates, true)) {
        return this.allowDates.includes(event)
      }
      return true
    },
    _isValidAllowDate(date) {
      const check = this._isAllowDate(date)
      return check || this.$t('message.isNotAllowedDate')
    },
    _eventColor(event) {
      if (this.$validate.DataValid(this.holidayDates, true) && this.holidayDates.includes(event)) {
        return 'holiday'
      }
    },
  },
}
</script>
